/**
 * Content Security Policy (CSP) Configuration Utility
 *
 * This utiltiy automatically generates the appropriate CSP header based on your current environment.
 * The CSP is automatically applied in `app/root.tsx`:
 * Production uses the base CSP with no additional overrides for maximum security.
 * Development and Testing environments allow additional sources for development and testing.
 * Make sure you test changes in a browser for each environment to ensure it is working as intended.
 *
 */

import { EnvEnum, ENVIRONMENT } from './constants'

// Base CSP policy that applies to all environments
const BASE_CSP = {
    'default-src': [
        "'self'",
        'https://api.production.fig.ca',
        'https://content.fig.ca',
        'https://my.fig.ca',
        'https://fig-prod-api.private.fin.ag',
        'https://fig-alterna-prod-api.private.fin.ag',
        'https://maps.googleapis.com',
        'https://sdk.onfido.com',
        'https://assets.onfido.com',
        'https://*.online-metrix.net',
        'https://*.posthog.com',
        'https://ph.fig.ca',
        'https://diverse-kodiak-54.clerk.accounts.dev'
    ],
    'script-src': [
        "'self'",
        "'unsafe-inline'",
        "'unsafe-eval'",
        'https://uptime.betterstack.com',
        'https://use.typekit.net',
        'https://clerk.fig.ca',
        'https://eminent-rhino-23.clerk.accounts.dev', // TODO: remove post launch
        'https://diverse-kodiak-54.clerk.accounts.dev', // TODO: remove post launch
        'https://challenges.cloudflare.com',
        'https://api.production.fig.ca',
        'https://content.fig.ca',
        'https://static.production.fig.ca',
        'https://*.online-metrix.net',
        'https://sdk.onfido.com',
        'https://assets.onfido.com',
        'https://*.trustarc.com',
        'https://maps.googleapis.com',
        'https://fig-help.freshchat.com',
        'https://widget.trustpilot.com',
        'https://*.googletagmanager.com',
        'https://googletagmanager.com',
        'https://tagmanager.google.com',
        'https://connect.facebook.net',
        'https://connect.facebook.net/en_US/fbevents.js',
        'https://www.redditstatic.com',
        'https://c.amazon-adsystem.com',
        'https://ph.fig.ca',
        /** PostHog lazy-loads recorder, surveys, web-vitals, etc. from the regional assets CDN (not covered by `*.posthog.com` alone). */
        'https://us-assets.i.posthog.com',
        'https://*.i.posthog.com',
        'blob:',
        /** Braze Web SDK: `public/service-worker.js` uses `importScripts` for push (js.appboycdn.com). */
        'https://js.appboycdn.com'
    ],
    'style-src': [
        "'self'",
        "'unsafe-inline'",
        'https://p.typekit.net',
        'https://fonts.googleapis.com',
        'https://use.typekit.net',
        'https://use.typekit.net/zwf3tbr.css',
        'https://uptime.betterstack.com',
        'https://sdk.onfido.com',
        'https://widget.trustpilot.com',
        'https://consent-pref.trustarc.com',
        'https://diverse-kodiak-54.clerk.accounts.dev',
        'https://fig-help.freshchat.com',
        'https://maps.googleapis.com',
        'https://maps.gstatic.com',
        'https://*.gstatic.com',
        'https://googletagmanager.com',
        'https://tagmanager.google.com',
        /** Braze in-app messages / SDK may inject Font Awesome kit CSS */
        'https://use.fontawesome.com'
    ],
    'font-src': ["'self'", 'https://fonts.gstatic.com', 'https://use.typekit.net', 'https://use.fontawesome.com', 'https://ka-f.fontawesome.com'],
    'img-src': [
        "'self'",
        'https://img.clerk.com',
        'https://widget.trustpilot.com',
        'https://consent-pref.trustarc.com',
        'https://sdk.onfido.com',
        'https://consent.trustarc.com',
        'https://consent.truste.com',
        'https://*.fig.ca',
        'https://*.online-metrix.net',
        'https://fig.ca',
        'https://*.googletagmanager.com',
        'https://googletagmanager.com',
        'https://*.google-analytics.com',
        'https://*.g.doubleclick.net',
        'https://*.google.com',
        'https://google.com',
        'https://www.google.com',
        'https://*.google.ca',
        'https://www.google.ca',
        'https://maps.gstatic.com',
        'https://*.gstatic.com',
        'https://ssl.gstatic.com',
        'https://www.gstatic.com',
        'https://connect.facebook.net',
        'https://connect.facebook.net/en_US/fbevents.js',
        'https://www.facebook.com',
        'https://www.redditstatic.com',
        'https://www.reddit.com',
        'https://events.redditmedia.com',
        'https://alb.reddit.com',
        'https://*.amazon-adsystem.com',
        'https://p.typekit.net',
        'data:'
    ],
    'connect-src': [
        "'self'",
        'https://clerk.fig.ca',
        'https://eminent-rhino-23.clerk.accounts.dev', // TODO: remove post launch
        'https://diverse-kodiak-54.clerk.accounts.dev', // TODO: remove post launch
        'https://clerk-telemetry.com',
        'https://*.clerk-telemetry.com',
        'https://uptime.betterstack.com',
        'https://api.production.fig.ca',
        'https://content.fig.ca',
        'https://*.fig.ca',
        'https://maps.googleapis.com',
        'https://assets.onfido.com',
        'https://api.onfido.com',
        'https://api.us.onfido.com',
        'https://api.ca.onfido.com',
        'https://sdk.onfido.com',
        'wss://sync.onfido.com',
        'https://*.trustarc.com',
        'https://*.sentry.io',
        'https://*.googletagmanager.com',
        'https://www.googletagmanager.com',
        'https://*.google-analytics.com',
        'https://*.analytics.google.com',
        'https://*.g.doubleclick.net',
        'https://*.google.com',
        'https://google.com',
        'https://www.google.com',
        'https://*.google.ca',
        'https://www.google.ca',
        'https://pagead2.googlesyndication.com',
        'https://connect.facebook.net',
        'https://connect.facebook.net/en_US/fbevents.js',
        'https://www.facebook.com',
        'https://www.reddit.com',
        'https://www.redditstatic.com',
        'https://events.redditmedia.com',
        'https://ads-api.reddit.com',
        'https://alb.reddit.com',
        'https://pixel-config.reddit.com',
        'https://conversions-config.reddit.com',
        'https://*.amazon-adsystem.com',
        'https://ara.paa-reporting-advertising.amazon',
        'https://sdk.iad-07.braze.com',
        'https://us-assets.i.posthog.com',
        'https://us.i.posthog.com',
        'https://ph.fig.ca',
        'data:'
    ],
    'frame-src': [
        "'self'",
        'https://challenges.cloudflare.com',
        'https://fig-prod-iframe.private.fin.ag',
        'https://fig-alterna-prod-iframe.private.fin.ag',
        'https://content.fig.ca',
        'https://fig-help.freshchat.com',
        'https://fig-support.webpush.freshchat.com',
        'https://*.online-metrix.net',
        'https://sdk.onfido.com',
        'https://widget.trustpilot.com',
        'https://consent-pref.trustarc.com',
        'https://diverse-kodiak-54.clerk.accounts.dev',
        'https://figgystatic.production.fig.ca',
        'https://www.googletagmanager.com'
    ],
    'object-src': ["'none'"],
    'base-uri': ["'self'"],
    'form-action': ["'self'", 'https://*.fig.ca', 'https://fig.ca'],
    'worker-src': ["'self'", 'blob:', 'data:']
}

// Environment-specific CSP overrides
const ENVIRONMENT_CSP_OVERRIDES: Record<EnvEnum, Partial<typeof BASE_CSP>> = {
    [EnvEnum.PRODUCTION]: {
        // ⚠️ Production uses base CSP with no additional overrides for maximum security
    },
    [EnvEnum.LOCAL]: {
        'default-src': [...BASE_CSP['default-src'], 'http://localhost:*', 'https://fig-dev-api.private.fin.ag', 'https://*.clerk.accounts.dev'],
        'frame-src': [...BASE_CSP['frame-src'], 'https://fig-dev-iframe.private.fin.ag', 'https://*.clerk.accounts.dev', 'https://figgystatic.staging.fig.ca'],
        'style-src': [...BASE_CSP['style-src'], 'https://*.clerk.accounts.dev'],
        'script-src': [...BASE_CSP['script-src'], 'https://*.clerk.accounts.dev'],
        'connect-src': [...BASE_CSP['connect-src'], 'http://localhost:*', 'https://api.preprod.fig.ca', 'https://*.clerk.accounts.dev']
    },
    [EnvEnum.DEVELOPMENT]: {
        'default-src': [...BASE_CSP['default-src'], 'http://localhost:*', 'https://fig-dev-api.private.fin.ag', 'https://*.clerk.accounts.dev'],
        'frame-src': [...BASE_CSP['frame-src'], 'https://fig-dev-iframe.private.fin.ag', 'https://*.clerk.accounts.dev', 'https://figgystatic.staging.fig.ca'],
        'style-src': [...BASE_CSP['style-src'], 'https://*.clerk.accounts.dev'],
        'script-src': [...BASE_CSP['script-src'], 'https://*.clerk.accounts.dev'],
        'connect-src': [...BASE_CSP['connect-src'], 'http://localhost:*', 'https://api.preprod.fig.ca', 'https://*.clerk.accounts.dev']
    },
    [EnvEnum.STAGING]: {
        'default-src': [
            ...BASE_CSP['default-src'],
            'https://api.staging.fig.ca',
            'https://api.preprod.fig.ca',
            'https://fig-preprod-api.private.fin.ag',
            'https://fig-staging-api.private.fin.ag',
            'https://*.clerk.accounts.dev'
        ],
        'frame-src': [
            ...BASE_CSP['frame-src'],
            'https://api.staging.fig.ca',
            'https://api.preprod.fig.ca',
            'https://fig-preprod-iframe.private.fin.ag',
            'https://fig-staging-iframe.private.fin.ag',
            'https://*.clerk.accounts.dev',
            'https://figgystatic.staging.fig.ca'
        ],
        'style-src': [...BASE_CSP['style-src'], 'https://*.clerk.accounts.dev'],
        'script-src': [...BASE_CSP['script-src'], 'https://api.staging.fig.ca', 'https://api.preprod.fig.ca', 'https://*.clerk.accounts.dev'],
        'connect-src': [...BASE_CSP['connect-src'], 'https://api.staging.fig.ca', 'https://api.preprod.fig.ca', 'https://*.clerk.accounts.dev']
    },
    [EnvEnum.PREPROD]: {
        'default-src': [
            ...BASE_CSP['default-src'],
            'https://api.staging.fig.ca',
            'https://api.preprod.fig.ca',
            'https://fig-preprod-api.private.fin.ag',
            'https://fig-staging-api.private.fin.ag',
            'https://*.clerk.accounts.dev'
        ],
        'frame-src': [
            ...BASE_CSP['frame-src'],
            'https://api.staging.fig.ca',
            'https://api.preprod.fig.ca',
            'https://fig-preprod-iframe.private.fin.ag',
            'https://fig-staging-iframe.private.fin.ag',
            'https://*.clerk.accounts.dev',
            'https://figgystatic.staging.fig.ca'
        ],
        'style-src': [...BASE_CSP['style-src'], 'https://*.clerk.accounts.dev'],
        'script-src': [...BASE_CSP['script-src'], 'https://api.staging.fig.ca', 'https://api.preprod.fig.ca', 'https://*.clerk.accounts.dev'],
        'connect-src': [...BASE_CSP['connect-src'], 'https://api.staging.fig.ca', 'https://api.preprod.fig.ca', 'https://*.clerk.accounts.dev']
    },
    [EnvEnum.PREVIEW]: {
        'default-src': [...BASE_CSP['default-src'], 'https://*.onrender.com', 'https://fig-dev-api.private.fin.ag', 'https://*.clerk.accounts.dev'],
        'frame-src': [
            ...BASE_CSP['frame-src'],
            'https://fig-dev-iframe.private.fin.ag',
            'https://*.clerk.accounts.dev',
            'https://figgystatic.staging.fig.ca',
            'https://sentry.io',
            'https://*.sentry.io'
        ],
        'style-src': [...BASE_CSP['style-src'], 'https://*.clerk.accounts.dev'],
        'script-src': [
            ...BASE_CSP['script-src'],
            'https://api.staging.fig.ca',
            'https://api.preprod.fig.ca',
            'https://*.clerk.accounts.dev',
            'https://browser.sentry-cdn.com'
        ],
        'connect-src': [...BASE_CSP['connect-src'], 'https://*.onrender.com', 'https://api.preprod.fig.ca', 'https://*.clerk.accounts.dev']
    },
    [EnvEnum.TESTING]: {}
}

/**
 * Generates a Content Security Policy string based on the current environment
 * @returns CSP string ready to be used in a meta tag
 */
export default function generateCSP(): string {
    const environmentOverrides = ENVIRONMENT_CSP_OVERRIDES[ENVIRONMENT] || {}

    // Merge base CSP with environment-specific overrides
    const finalCSP = { ...BASE_CSP, ...environmentOverrides }

    // Convert to CSP string format
    return Object.entries(finalCSP)
        .map(([directive, sources]) => `${directive} ${sources.join(' ')}`)
        .join('; ')
}
